import React, { useState, useRef, useEffect } from 'react';
import { Star, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { FilterSection, GroupByOption, SavedSearch } from '../types';

interface FiltersPanelProps {
  filterSections: FilterSection[];
  groupByOptions: GroupByOption[];
  selectedGroupBy: string;
  onGroupByChange?: (value: string) => void;
  savedSearches: SavedSearch[];
  onSaveCurrentSearch?: () => void;
  onApplySavedSearch?: (search: SavedSearch) => void;
  onClose: () => void;
}

export const FiltersPanel: React.FC<FiltersPanelProps> = ({
  filterSections,
  groupByOptions,
  selectedGroupBy,
  onGroupByChange,
  savedSearches,
  onSaveCurrentSearch,
  onApplySavedSearch,
  onClose
}) => {
  const hasActiveFilters = filterSections.some(section => section.selectedValues.length > 0);
  const activeFilterCount = filterSections.reduce((count, section) => count + section.selectedValues.length, 0);
  
  // Favorites dropdown state
  const [showFavoritesDropdown, setShowFavoritesDropdown] = useState(false);
  const favoritesButtonRef = useRef<HTMLButtonElement>(null);
  
  // Search states for different sections
  const [buildingSearchTerm, setBuildingSearchTerm] = useState('');
  const [unitSearchTerm, setUnitSearchTerm] = useState('');
  const [labelSearchTerm, setLabelSearchTerm] = useState('');
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectPartSearchTerm, setProjectPartSearchTerm] = useState('');

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (favoritesButtonRef.current && !favoritesButtonRef.current.contains(event.target as Node)) {
        const dropdown = document.getElementById('favorites-dropdown');
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setShowFavoritesDropdown(false);
        }
      }
    };

    if (showFavoritesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFavoritesDropdown]);

  return (
    <div className="w-full overflow-hidden">
      {/* Compact Header */}
      <div className="px-4 py-2 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">Filters</h3>
              {hasActiveFilters && (
                <Badge variant="secondary" className="bg-moss-primary/10 text-moss-primary">
                  {activeFilterCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Clear all filters
                    filterSections.forEach(section => section.onUpdate([]));
                    onGroupByChange?.('none');
                  }}
                  className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                >
                  Wissen
                </Button>
              )}
              
              {/* Favorites Dropdown */}
              <div className="relative">
                <Button
                  ref={favoritesButtonRef}
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowFavoritesDropdown(!showFavoritesDropdown)}
                  className={cn(
                    "h-8 w-8",
                    showFavoritesDropdown && "bg-yellow-500/10 text-yellow-600"
                  )}
                >
                  <Star className="h-4 w-4" />
                  {savedSearches.length > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-yellow-500/20 text-yellow-700 border-yellow-300">
                      {savedSearches.length}
                    </Badge>
                  )}
                </Button>

                {/* Favorites Dropdown Menu */}
                {showFavoritesDropdown && (
                  <div 
                    id="favorites-dropdown"
                    className="absolute top-full right-0 mt-2 w-64 bg-card rounded-lg shadow-xl border border-border z-70 overflow-hidden"
                  >
                    <div className="p-3 border-b border-border bg-muted/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span className="text-xs font-medium">Favorieten</span>
                      </div>
                      {onSaveCurrentSearch && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onSaveCurrentSearch();
                            setShowFavoritesDropdown(false);
                            onClose();
                          }} 
                          className="w-full h-8 text-xs bg-moss-primary/5 hover:bg-moss-primary/10 text-moss-primary border-moss-primary/30"
                        >
                          + Huidige zoekopdracht opslaan
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="max-h-48 p-2">
                      {savedSearches.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="text-xs text-muted-foreground">Geen favorieten</div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {savedSearches.map(entry => (
                            <Button 
                              key={entry.id}
                              variant="ghost"
                              onClick={() => {
                                onApplySavedSearch?.(entry);
                                setShowFavoritesDropdown(false);
                                onClose();
                              }} 
                              className="w-full justify-start h-auto py-2 text-xs"
                            >
                              <Star className="h-3 w-3 text-yellow-500 mr-2 shrink-0" />
                              <div className="truncate">{entry.name}</div>
                            </Button>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
          {/* Filters Section */}
          {filterSections.length > 0 && (
            <div className="p-5 overflow-hidden">
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-6 pr-2">
                  {filterSections.map((section) => {
                    const selectedCount = section.selectedValues.length;
                    const isSearchableSection = section.key === 'building' || section.key === 'unit' || section.key === 'label' || section.key === 'supplier' || section.key === 'assignee' || section.key === 'project' || section.key === 'projectPart';
                    const currentSearchTerm = section.key === 'building' ? buildingSearchTerm
                      : section.key === 'unit' ? unitSearchTerm
                      : section.key === 'label' ? labelSearchTerm
                      : section.key === 'supplier' ? supplierSearchTerm
                      : section.key === 'assignee' ? assigneeSearchTerm
                      : section.key === 'project' ? projectSearchTerm
                      : section.key === 'projectPart' ? projectPartSearchTerm
                      : '';
                    
                    // For searchable sections, filter options based on search term (search both key and label)
                    const searchFilteredOptions = isSearchableSection && currentSearchTerm
                      ? section.options.filter(option => {
                          const term = currentSearchTerm.toLowerCase();
                          return option.label.toLowerCase().includes(term) ||
                            option.key.toLowerCase().includes(term);
                        })
                      : section.options;
                    
                    // For searchable sections, only show non-selected options in search results
                    const availableOptions = isSearchableSection 
                      ? searchFilteredOptions.filter(option => !section.selectedValues.includes(option.key))
                      : section.options;
                    
                    return (
                      <div key={section.key} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-medium">{section.label}</div>
                          <Separator className="flex-1" />
                          {selectedCount > 0 && (
                            <Badge variant="secondary" className="text-xs h-5 px-1.5">
                              {selectedCount}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Search Input for searchable sections (Building, Unit, Label, Supplier, Assignee, Project, ProjectPart) */}
                        {isSearchableSection && (
                          <div className="relative">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="text"
                              placeholder={
                                section.key === 'building' ? 'Zoek en selecteer gebouw...'
                                : section.key === 'unit' ? 'Zoek en selecteer unit...'
                                : section.key === 'label' ? 'Zoek en selecteer label...'
                                : section.key === 'supplier' ? 'Zoek en selecteer leverancier...'
                                : section.key === 'assignee' ? 'Zoek en selecteer persoon...'
                                : section.key === 'project' ? 'Zoek en selecteer project...'
                                : section.key === 'projectPart' ? 'Zoek en selecteer onderdeel...'
                                : ''
                              }
                              value={currentSearchTerm}
                              onChange={(e) => {
                                if (section.key === 'building') {
                                  setBuildingSearchTerm(e.target.value);
                                } else if (section.key === 'unit') {
                                  setUnitSearchTerm(e.target.value);
                                } else if (section.key === 'label') {
                                  setLabelSearchTerm(e.target.value);
                                } else if (section.key === 'supplier') {
                                  setSupplierSearchTerm(e.target.value);
                                } else if (section.key === 'assignee') {
                                  setAssigneeSearchTerm(e.target.value);
                                } else if (section.key === 'project') {
                                  setProjectSearchTerm(e.target.value);
                                } else if (section.key === 'projectPart') {
                                  setProjectPartSearchTerm(e.target.value);
                                }
                              }}
                              className="pl-9 pr-9"
                            />
                            {currentSearchTerm && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (section.key === 'building') {
                                    setBuildingSearchTerm('');
                                  } else if (section.key === 'unit') {
                                    setUnitSearchTerm('');
                                  } else if (section.key === 'label') {
                                    setLabelSearchTerm('');
                                  } else if (section.key === 'supplier') {
                                    setSupplierSearchTerm('');
                                  } else if (section.key === 'assignee') {
                                    setAssigneeSearchTerm('');
                                  } else if (section.key === 'project') {
                                    setProjectSearchTerm('');
                                  } else if (section.key === 'projectPart') {
                                    setProjectPartSearchTerm('');
                                  }
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {/* Selected Items as Removable Chips */}
                        {isSearchableSection && selectedCount > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {section.selectedValues.map((selectedKey) => {
                              const option = section.options.find(opt => opt.key === selectedKey);
                              if (!option) return null;

                              const colorClass = section.color === 'moss' ? 'bg-moss-primary/10 text-moss-primary border-moss-primary/30'
                                : section.color === 'brick' ? 'bg-brick-primary/10 text-brick-primary border-brick-primary/30'
                                : section.color === 'yellow' ? 'bg-yellow-500/10 text-yellow-700 border-yellow-300'
                                : 'bg-muted text-muted-foreground border-border';

                              // Use custom renderer if provided, otherwise use default
                              const customRenderer = section.renderOption || option.renderOption;

                              return (
                                <Badge
                                  key={selectedKey}
                                  variant="outline"
                                  className={cn("gap-1", colorClass)}
                                >
                                  {customRenderer ? (
                                    <span className="select-none flex items-center gap-1">{customRenderer(option)}</span>
                                  ) : (
                                    <span className="select-none">{option.label}</span>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      const next = section.selectedValues.filter(s => s !== selectedKey);
                                      section.onUpdate(next);
                                    }}
                                    className="h-3 w-3 p-0 hover:bg-transparent"
                                    title={`Verwijder ${option.label}`}
                                  >
                                    <X className="h-2 w-2" />
                                  </Button>
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Search Results for Adding New Items */}
                        {isSearchableSection && currentSearchTerm && (
                          <div className="space-y-2">
                            {availableOptions.length > 0 ? (
                              <>
                                <div className="text-xs text-muted-foreground">Klik om toe te voegen:</div>
                                <ScrollArea className="max-h-32">
                                  <div className="flex flex-wrap gap-1.5">
                                    {availableOptions.slice(0, 10).map((option) => {
                                      // Use custom renderer if provided, otherwise use default
                                      const customRenderer = section.renderOption || option.renderOption;

                                      return (
                                        <Button
                                          key={option.key}
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const next = [...section.selectedValues, option.key];
                                            section.onUpdate(next);
                                            // Clear search after selection
                                            if (section.key === 'building') {
                                              setBuildingSearchTerm('');
                                            } else if (section.key === 'unit') {
                                              setUnitSearchTerm('');
                                            } else if (section.key === 'label') {
                                              setLabelSearchTerm('');
                                            } else if (section.key === 'supplier') {
                                              setSupplierSearchTerm('');
                                            } else if (section.key === 'assignee') {
                                              setAssigneeSearchTerm('');
                                            } else if (section.key === 'project') {
                                              setProjectSearchTerm('');
                                            } else if (section.key === 'projectPart') {
                                              setProjectPartSearchTerm('');
                                            }
                                          }}
                                          className="h-7 text-xs"
                                        >
                                          {customRenderer ? (
                                            customRenderer(option)
                                          ) : (
                                            <>
                                              <span className="text-brick-primary mr-1">+</span>
                                              {option.label}
                                            </>
                                          )}
                                        </Button>
                                      );
                                    })}
                                    {availableOptions.length > 10 && (
                                      <div className="text-xs text-muted-foreground py-1">
                                        ... en {availableOptions.length - 10} meer
                                      </div>
                                    )}
                                  </div>
                                </ScrollArea>
                              </>
                            ) : (
                              <div className="text-xs text-muted-foreground py-2">
                                Geen {
                                  section.key === 'building' ? 'gebouwen'
                                  : section.key === 'unit' ? 'units'
                                  : section.key === 'label' ? 'labels'
                                  : section.key === 'supplier' ? 'leveranciers'
                                  : section.key === 'assignee' ? 'personen'
                                  : section.key === 'project' ? 'projecten'
                                  : section.key === 'projectPart' ? 'onderdelen'
                                  : 'resultaten'
                                } gevonden voor "{currentSearchTerm}"
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Regular Filter Buttons for Non-Searchable Sections */}
                        {!isSearchableSection && (
                          <div className="flex flex-wrap gap-1.5">
                            {section.options.map((option) => {
                              const isSelected = section.selectedValues.includes(option.key);
                              const colorClass = section.color === 'moss' ? 'bg-moss-primary/10 text-moss-primary border-moss-primary/30'
                                : section.color === 'brick' ? 'bg-brick-primary/10 text-brick-primary border-brick-primary/30'
                                : section.color === 'yellow' ? 'bg-yellow-500/10 text-yellow-600 border-yellow-300'
                                : 'bg-muted text-muted-foreground border-border';

                              return (
                                <Button
                                  key={option.key}
                                  variant={isSelected ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    let next: string[];
                                    if (section.singleSelect) {
                                      // Single-select: toggle selection or replace with new selection
                                      next = isSelected ? [] : [option.key];
                                    } else {
                                      // Multi-select: add/remove from array
                                      next = isSelected
                                        ? section.selectedValues.filter(s => s !== option.key)
                                        : [...section.selectedValues, option.key];
                                    }
                                    section.onUpdate(next);
                                  }}
                                  className={cn(
                                    "h-7 text-xs",
                                    isSelected && colorClass
                                  )}
                                >
                                  {option.label}
                                  {isSelected && <span className="ml-1">✓</span>}
                                </Button>
                              );
                            })}
                          </div>
                        )}

                        {/* Optional section footer (e.g. quick toggles like "Toon tickets zonder label") */}
                        {section.footer}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Group By Section */}
          {groupByOptions.length > 0 && (
            <div className="p-4 min-w-[200px] overflow-hidden">
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-4 pr-2">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium">Groeperen</div>
                      <Separator className="flex-1" />
                      {selectedGroupBy && selectedGroupBy !== 'none' && (
                        <Badge variant="secondary" className="text-xs h-5 px-1.5">
                          1
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {groupByOptions
                        .filter(option => option.key !== 'none' && option.key !== 'geen') // Remove 'none'/'geen' options since they're redundant with toggle behavior
                        .map((option) => {
                        const isSelected = selectedGroupBy === option.key;
                        const colorClass = 'bg-moss-primary/10 text-moss-primary border-moss-primary/30';
                        
                        return (
                          <Button
                            key={option.key}
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              // Toggle functionality - if already selected, deselect (set to 'none'), otherwise select
                              const nextValue = isSelected ? 'none' : option.key;
                              onGroupByChange?.(nextValue);
                            }}
                            className={cn(
                              "h-7 text-xs",
                              isSelected && colorClass
                            )}
                          >
                            {option.label}
                            {isSelected && <span className="ml-1">✓</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
    </div>
  );
};
